import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Code, CheckCircle } from 'lucide-react';
import { UserState } from '../utils/dashboardTypes';
import { StateOverride, setStateOverride, getStateOverride, isOverrideActive } from '../utils/devStateOverride';

interface DevStateToggleProps {
  currentState: UserState;
  onStateChange: (state: StateOverride) => void;
  className?: string;
}

const ToggleContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToggleButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  background: ${props => props.$isActive ? '#fef3c7' : '#f3f4f6'};
  border: 1px solid ${props => props.$isActive ? '#fbbf24' : '#d1d5db'};
  color: ${props => props.$isActive ? '#92400e' : '#6b7280'};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease-in-out;

  &:hover {
    background: ${props => props.$isActive ? '#fde68a' : '#e5e7eb'};
    border-color: ${props => props.$isActive ? '#f59e0b' : '#9ca3af'};
  }

  &:focus-visible {
    outline: 2px solid #227B7E;
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  animation: slideDown 200ms ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DropdownHeader = styled.div`
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: #fbbf24;
  }
`;

const StateOption = styled.button<{ $isActive: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
  border: none;
  background: ${props => props.$isActive ? '#f0fdf4' : 'transparent'};
  color: ${props => props.$isActive ? '#15803d' : '#212529'};
  font-size: 0.875rem;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  text-align: left;
  cursor: pointer;
  transition: background 150ms ease-in-out;

  &:hover {
    background: ${props => props.$isActive ? '#dcfce7' : '#f8f9fa'};
  }

  &:first-of-type {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  &:last-of-type {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }

  &:focus-visible {
    outline: 2px solid #227B7E;
    outline-offset: -2px;
  }
`;

const StateLabel = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const StateName = styled.span`
  font-size: 0.875rem;
`;

const StateDescription = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 400;
`;

const CheckIcon = styled(CheckCircle)`
  color: #15803d;
  width: 16px;
  height: 16px;
`;

const stateOptions: Array<{
  value: StateOverride;
  label: string;
  description: string;
}> = [
  { value: 'auto', label: 'Auto', description: 'Computed from metrics' },
  { value: 'new_user', label: 'New User', description: 'Onboarding view' },
  { value: 'empty_state', label: 'Empty State', description: 'Has ATS, no data' },
  { value: 'growth_mode', label: 'Growth Mode', description: 'Simplified metrics' },
  { value: 'established_user', label: 'Established', description: 'Full dashboard' },
];

export function DevStateToggle({ currentState, onStateChange, className = '' }: DevStateToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const activeOverride = getStateOverride();
  const isActive = isOverrideActive();

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleStateSelect = (state: StateOverride) => {
    setStateOverride(state);
    onStateChange(state);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen) {
        const target = e.target as HTMLElement;
        if (!target.closest('[data-dev-toggle]')) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const currentLabel = stateOptions.find(opt => opt.value === activeOverride)?.label || 'Auto';

  return (
    <ToggleContainer className={className} data-dev-toggle>
      <ToggleButton
        $isActive={isActive}
        onClick={handleToggle}
        title="Dev Mode: Override Dashboard State"
      >
        <Code />
        <span>Dev: {currentLabel}</span>
      </ToggleButton>

      <Dropdown $isOpen={isOpen}>
        <DropdownHeader>
          <Code size={14} />
          Dashboard State Override
        </DropdownHeader>
        {stateOptions.map(option => (
          <StateOption
            key={option.value}
            $isActive={activeOverride === option.value}
            onClick={() => handleStateSelect(option.value)}
          >
            <StateLabel>
              <StateName>{option.label}</StateName>
              <StateDescription>{option.description}</StateDescription>
            </StateLabel>
            {activeOverride === option.value && <CheckIcon />}
          </StateOption>
        ))}
      </Dropdown>
    </ToggleContainer>
  );
}
